import Button from '@ingka/button';
import InlineMessage from '@ingka/inline-message';
import InputField from '@ingka/input-field';
import Text from '@ingka/text';
import '@ingka/inline-message/dist/style.css';
import { useEffect, useState } from 'react';
import styles from './CatalogItemModal.module.css';

export interface CatalogItemFormData {
  id: string;
  name: string;
  description: string;
}

interface Props {
  title: string;
  mode: 'create' | 'edit';
  initial?: Partial<CatalogItemFormData> & { active?: boolean; ordering?: number };
  warningBody?: string; // shown as a cautionary message on create
  onClose: () => void;
  onSave: (data: CatalogItemFormData & { active?: boolean; ordering?: number }) => Promise<void>;
}

function normalizeId(input: string): string {
  return input.trim().toLowerCase().replace(/\s+/g, '-');
}

function validateId(id: string): string | null {
  if (!id) return 'ID is required';
  if (id.length > 64) return 'ID cannot exceed 64 characters';
  if (!/^[a-z]/.test(id)) return 'ID must start with a letter';
  if (!/^[a-z0-9-]+$/.test(id)) {
    return 'ID can only contain lowercase letters, digits, and dashes';
  }
  if (id.startsWith('-') || id.endsWith('-')) {
    return 'ID cannot start or end with a dash';
  }
  if (id.includes('--')) return 'ID cannot contain consecutive dashes';
  return null;
}

export default function CatalogItemModal({
  title,
  mode,
  initial,
  warningBody,
  onClose,
  onSave,
}: Props) {
  const [name, setName] = useState(initial?.name ?? '');
  const [id, setId] = useState(initial?.id ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [active, setActive] = useState(initial?.active ?? true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [idManuallyEdited, setIdManuallyEdited] = useState(false);

  // Auto-derive ID from name in create mode, unless the admin has manually edited it
  useEffect(() => {
    if (mode === 'create' && !idManuallyEdited) {
      setId(normalizeId(name));
    }
  }, [name, mode, idManuallyEdited]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleSave = async () => {
    const newErrors: Record<string, string> = {};
    const normalizedId = normalizeId(id);

    if (!name.trim()) newErrors.name = 'Name is required';
    if (name.length > 128) newErrors.name = 'Name cannot exceed 128 characters';
    if (mode === 'create') {
      const idError = validateId(normalizedId);
      if (idError) newErrors.id = idError;
    }
    if (description.length > 1000)
      newErrors.description = 'Description cannot exceed 1000 characters';

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setSaving(true);
    setSubmitError(null);
    try {
      await onSave({
        id: normalizedId,
        name: name.trim(),
        description: description.trim(),
        active,
      });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: escape key handled globally via useEffect */}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: backdrop is decorative; interactive elements inside handle keyboard */}
      <div className={styles.backdrop} onClick={onClose}>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: click only stops backdrop-close bubbling */}
      <div
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="catalog-modal-title"
      >
        <div className={styles.header}>
          <Text tagName="h2" textStyle="Heading.M" id="catalog-modal-title">
            {title}
          </Text>
          <button type="button" aria-label="Close" className={styles.closeBtn} onClick={onClose}>
            ×
          </button>
        </div>

        <div className={styles.body}>
          {warningBody && mode === 'create' && (
            <div className={styles.warning}>
              <InlineMessage variant="cautionary" title="Before you add this" body={warningBody} />
            </div>
          )}

          <div className={styles.field}>
            <InputField
              id="catalog-name"
              label="Name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={saving}
            />
            {errors.name && <div className={styles.fieldError}>{errors.name}</div>}
          </div>

          <div className={styles.field}>
            <InputField
              id="catalog-id"
              label="ID"
              type="text"
              value={id}
              onChange={(e) => {
                setId(e.target.value);
                setIdManuallyEdited(true);
              }}
              disabled={saving || mode === 'edit'}
            />
            {mode === 'create' && (
              <div className={styles.fieldHint}>
                Lowercase letters, digits, and dashes only. Auto-generated from name.
              </div>
            )}
            {mode === 'edit' && (
              <div className={styles.fieldHint}>ID cannot be changed after creation.</div>
            )}
            {errors.id && <div className={styles.fieldError}>{errors.id}</div>}
          </div>

          <div className={styles.field}>
            <InputField
              id="catalog-description"
              label="Description (optional)"
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={saving}
            />
            {errors.description && <div className={styles.fieldError}>{errors.description}</div>}
          </div>

          {mode === 'edit' && (
            <div className={styles.field}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                  disabled={saving}
                />
                <span>Active</span>
              </label>
              <div className={styles.fieldHint}>
                Uncheck to soft-delete. Existing uploads remain unaffected.
              </div>
            </div>
          )}

          {submitError && (
            <div className={styles.submitError}>
              <InlineMessage variant="negative" title="Save failed" body={submitError} />
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <Button type="tertiary" text="Cancel" disabled={saving} onClick={onClose} />
          <Button
            type="emphasised"
            text={saving ? 'Saving…' : 'Save'}
            disabled={saving}
            onClick={handleSave}
          />
        </div>
      </div>
    </div>
   </>
  );
}
